/**
 * mapApi — thin JSON client for Reader::MapChaptersController (TASK-016
 * Phase 4's lazy per-chapter fetch). Mirrors tutorApi.ts's style exactly
 * (raw fetch + `credentials: 'same-origin'`, no CSRF header — this is a
 * plain GET, same as `fetchTutorConversations`/`fetchTutorBlockContent`).
 *
 *   GET /web/courses/:manual_version_id/map/chapters/:chapter_id
 *   -> { nodes: ConceptNode[], edges: ConceptEdge[], externalNodes: [...] }
 *   -> 404 when `chapter_id` matches no meso node of this release (task
 *      contract, Reader::ConceptGraphQuery#chapter doc) — surfaced here as
 *      `ChapterNotFoundError` so callers can tell "no such chapter" apart
 *      from a transient network/server failure.
 */

import type { ConceptEdge, ConceptNode } from '@arianna/shared'

export interface MapExternalNode {
  nodeId: string
  label: string
  canonicalName: string
  chapterId: string
}

export interface ChapterExplosion {
  nodes: ConceptNode[]
  edges: ConceptEdge[]
  externalNodes: MapExternalNode[]
}

export class ChapterNotFoundError extends Error {
  constructor(chapterId: string) {
    super(`Capitolo non trovato: ${chapterId}`)
    this.name = 'ChapterNotFoundError'
  }
}

/**
 * `chapterId` is the pipeline's macroId — a plain segment, but can contain
 * "." (task doc precaution on the route's `format: false`), so it's
 * `encodeURIComponent`-ed like any other path segment (unlike a `block_id`,
 * it never contains "/", so no per-piece splitting is needed here — compare
 * `fetchTutorBlockContent` in tutorApi.ts).
 */
export async function fetchChapterExplosion(
  manualVersionId: number,
  chapterId: string,
): Promise<ChapterExplosion> {
  const res = await fetch(
    `/web/courses/${manualVersionId}/map/chapters/${encodeURIComponent(chapterId)}`,
    {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    },
  )

  if (res.status === 404) throw new ChapterNotFoundError(chapterId)

  const data = (await res.json().catch(() => null)) as
    | (ChapterExplosion & { error?: string })
    | null
  if (!res.ok) {
    throw new Error(data?.error ?? `Richiesta mappa fallita: ${res.status}`)
  }
  return data as ChapterExplosion
}
